import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, FolderOpen } from 'lucide-react';
import OverlayWindow from '../ui-components/OverlayWindow';
import {
    getFolderById,
    removeFolderLink,
    setFolderPinned,
    FolderDetail,
    FolderLink,
} from '../../services/api/folderService';
import LoadingRing from '../ui-components/LoadingRing';
import AuthContext from '../../store/auth-context';
import PERMISSION_KEYS from '../../constants/permission-keys';
import { useToast } from '../../hooks/use-toast';
import { CreateEditFolderDialog } from './CreateEditFolderDialog';
import { DeleteFolderDialog } from './DeleteFolderDialog';
import { LinkedScannersDialog } from './LinkedScannersDialog';
import { ManageImplantsDialog } from './ManageImplantsDialog';
import { ManagePoliciesDialog } from './ManagePoliciesDialog';
import { ManageFilesDialog } from './ManageFilesDialog';
import MRIMachine from '../room-overview/MRIMachine';
import { LinkDialog } from './folderDetail/LinkDialog';
import { FolderNoteCard } from './folderDetail/FolderNoteCard';
import { FolderEditLog } from './FolderEditLog';
import { FolderDescription } from './folderDetail/FolderDescription';
import { FolderAddTarget, FolderHeader } from './folderDetail/FolderHeader';
import { SubFolderList } from './folderDetail/SubFolderList';
import { SubFolderTree } from './folderDetail/SubFolderTree';
import { FolderMembers } from './folderDetail/FolderMembers';
import { cn } from '../../lib/utils';

type ActiveDialog =
    | 'edit'
    | 'delete'
    | 'scanners'
    | 'implants'
    | 'policies'
    | 'files'
    | 'subfolder'
    | 'link';

export const FolderDetailPage = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const ctx = useContext(AuthContext);
    const { toast } = useToast();
    const canManage = ctx.hasPermission(PERMISSION_KEYS.manageGroups);
    const canUploadFiles = ctx.hasPermission(PERMISSION_KEYS.manageFiles);
    const [folder, setFolder] = useState<FolderDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeDialog, setActiveDialog] = useState<ActiveDialog | null>(null);
    const [pinPending, setPinPending] = useState(false);
    const [editingLink, setEditingLink] = useState<FolderLink | undefined>();
    const [removingLinkId, setRemovingLinkId] = useState<string | null>(null);
    const [noteEditing, setNoteEditing] = useState(false);
    const [selectedScannerId, setSelectedScannerId] = useState<string | null>(
        null
    );

    const openDialog = (dialog: ActiveDialog) => () => setActiveDialog(dialog);
    const handleOpenChange = (open: boolean) => {
        if (!open) setActiveDialog(null);
    };

    const fetchFolder = async () => {
        if (!id) return;
        try {
            const result = await getFolderById(id);
            setFolder(result);
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.detail.toasts.loadFailed'),
                variant: 'destructive',
            });
        }
    };

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!id) return;
            setLoading(true);
            setFolder(null);
            setNoteEditing(false);
            setActiveDialog(null);
            setEditingLink(undefined);
            setSelectedScannerId(null);
            try {
                const result = await getFolderById(id);
                if (!cancelled) setFolder(result);
            } catch {
                if (!cancelled) {
                    toast({
                        title: t('folders.common.error'),
                        description: t('folders.detail.toasts.loadFailed'),
                        variant: 'destructive',
                    });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [id]);

    const handleTogglePin = async () => {
        if (!folder) return;
        setPinPending(true);
        try {
            await setFolderPinned(folder._id, !folder.isPinned);
            await fetchFolder();
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.detail.toasts.pinFailed'),
                variant: 'destructive',
            });
        } finally {
            setPinPending(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <LoadingRing size="large" />
            </div>
        );
    }

    if (!folder) {
        return (
            <div className="w-full px-4 py-6 sm:px-6">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-xs text-subtle-foreground hover:text-foreground"
                >
                    <ChevronLeft className="h-3 w-3" aria-hidden="true" />
                    {t('folders.detail.backToHome')}
                </Link>
                <p className="mt-4 text-sm text-subtle-foreground">
                    {t('folders.detail.notFound')}
                </p>
            </div>
        );
    }

    const { implants, scanners, policies, files, links, subFolders } = folder;
    const childFolders = folder.parent ? [] : subFolders;
    const isFullyEmpty =
        implants.length === 0 &&
        scanners.length === 0 &&
        policies.length === 0 &&
        files.length === 0 &&
        links.length === 0 &&
        !folder.note &&
        !noteEditing;
    const treeRoot = folder.parent ?? {
        _id: folder._id,
        title: folder.title,
        category: folder.category,
    };

    const openLinkDialog = (link?: FolderLink) => {
        setEditingLink(link);
        setActiveDialog('link');
    };

    const handleAdd = (target: FolderAddTarget) =>
        target === 'link'
            ? openLinkDialog()
            : target === 'note'
              ? setNoteEditing(true)
              : setActiveDialog(target);

    const handleRemoveLink = async (link: FolderLink) => {
        setRemovingLinkId(link._id);
        try {
            await removeFolderLink(folder._id, link._id);
            await fetchFolder();
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.links.removeFailed'),
                variant: 'destructive',
            });
        } finally {
            setRemovingLinkId(null);
        }
    };

    return (
        <div className="mx-auto w-full max-w-5xl px-4 pb-32 pt-6 sm:px-6">
            <FolderHeader
                folder={folder}
                canManage={canManage}
                pinPending={pinPending}
                onTogglePin={handleTogglePin}
                onOpenEdit={openDialog('edit')}
                onOpenDelete={openDialog('delete')}
                onAdd={handleAdd}
            />

            <FolderDescription
                title={folder.title}
                description={folder.description}
            />

            <hr className="mt-4 border-subtle" />

            <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_16rem] lg:gap-x-8">
                <div className="flex min-w-0 flex-col gap-3.5 lg:col-start-1 lg:row-start-1">
                    {childFolders.length > 0 && (
                        <SubFolderList
                            subFolders={childFolders}
                            onCreate={
                                canManage ? openDialog('subfolder') : undefined
                            }
                        />
                    )}

                    <FolderNoteCard
                        folderId={folder._id}
                        note={folder.note}
                        canManage={canManage}
                        editing={noteEditing}
                        onEditingChange={setNoteEditing}
                        onSaved={fetchFolder}
                    />

                    {isFullyEmpty && childFolders.length === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-subtle py-16">
                            <FolderOpen
                                aria-hidden="true"
                                className="h-10 w-10 text-subtle-foreground"
                            />
                            <p className="text-sm font-medium text-foreground">
                                {t('folders.detail.empty.allTitle')}
                            </p>
                            {canManage && (
                                <p className="text-xs font-medium text-subtle-foreground">
                                    {t('folders.detail.empty.addHint')}
                                </p>
                            )}
                        </div>
                    ) : null}

                    <div
                        className={cn(
                            'flex flex-col gap-3.5',
                            (childFolders.length > 0 ||
                                folder.note ||
                                noteEditing) &&
                                'mt-6'
                        )}
                    >
                        <FolderMembers
                            folder={folder}
                            canManage={canManage}
                            onManage={setActiveDialog}
                            onSelectScanner={setSelectedScannerId}
                            onAddLink={() => openLinkDialog()}
                            onEditLink={openLinkDialog}
                            onRemoveLink={handleRemoveLink}
                            removingLinkId={removingLinkId}
                        />
                    </div>
                </div>

                <aside className="lg:col-start-2 lg:row-start-1">
                    <div className="flex flex-col gap-6 lg:sticky lg:top-8">
                        {subFolders.length > 0 && (
                            <SubFolderTree
                                root={treeRoot}
                                subFolders={subFolders}
                                currentId={folder._id}
                            />
                        )}
                        <FolderEditLog editLog={folder.editLog} />
                    </div>
                </aside>
            </div>

            {selectedScannerId && (
                <OverlayWindow
                    close={() => setSelectedScannerId(null)}
                    className="!bg-background md:!w-[55%] dark:!bg-neutral-900"
                >
                    <MRIMachine
                        machineId={selectedScannerId}
                        onClose={() => setSelectedScannerId(null)}
                        canEdit={false}
                    />
                </OverlayWindow>
            )}

            <LinkDialog
                folderId={folder._id}
                link={editingLink}
                open={activeDialog === 'link'}
                onOpenChange={handleOpenChange}
                onSaved={fetchFolder}
            />

            <CreateEditFolderDialog
                mode="edit"
                folder={folder}
                parent={folder.parent}
                open={activeDialog === 'edit'}
                onOpenChange={handleOpenChange}
                onSaved={fetchFolder}
            />

            <CreateEditFolderDialog
                mode="create"
                parent={{ _id: folder._id, title: folder.title }}
                open={activeDialog === 'subfolder'}
                onOpenChange={handleOpenChange}
            />

            <DeleteFolderDialog
                folderId={folder._id}
                folderTitle={folder.title}
                subFolders={childFolders}
                open={activeDialog === 'delete'}
                onOpenChange={handleOpenChange}
                onDeleted={() =>
                    navigate(
                        folder.parent ? `/group/${folder.parent._id}` : '/'
                    )
                }
            />

            <LinkedScannersDialog
                folderId={folder._id}
                existingScanners={scanners}
                open={activeDialog === 'scanners'}
                onOpenChange={handleOpenChange}
                onChanged={fetchFolder}
            />

            <ManageImplantsDialog
                folderId={folder._id}
                existingImplants={implants}
                open={activeDialog === 'implants'}
                onOpenChange={handleOpenChange}
                onChanged={fetchFolder}
            />

            <ManagePoliciesDialog
                folderId={folder._id}
                existingPolicies={policies}
                open={activeDialog === 'policies'}
                onOpenChange={handleOpenChange}
                onChanged={fetchFolder}
            />

            <ManageFilesDialog
                folderId={folder._id}
                existingFiles={files}
                canUpload={canUploadFiles}
                open={activeDialog === 'files'}
                onOpenChange={handleOpenChange}
                onChanged={fetchFolder}
            />
        </div>
    );
};
